import 'moment/locale/es';

import { Injectable } from '@angular/core';
import { PlansBundle } from '@common/models';
import { NextPayment } from '@common/models/plans.model';
import { PAID_PAYMENT_STATUSES, PROCESSING_PAYMENT_STATUSES } from '@constants/payment-status';
import moment, { Moment } from 'moment';

export type PaymentRenewalAlertTone = 'warning' | 'danger';
export type PaymentRenewalAlertIcon = 'alert-circle' | 'info';
/**
 * Qué hace el CTA del banner:
 * - 'register': flujo de pago normal (pago móvil / registrar pago).
 * - 'update-card': la tarjeta guardada fue rechazada de forma definitiva y hay
 *   que reemplazarla (rechazo terminal → `autoCharge.status = requires_card_update`).
 * - 'pay-card': el cobro necesita verificación 3DS del banco; el cliente debe
 *   completarla pagando on-session (`autoCharge.status = requires_action`).
 */
export type PaymentRenewalAlertAction = 'register' | 'update-card' | 'pay-card';

export interface PaymentRenewalAlertState {
    visible: boolean;
    dismissible: boolean;
    tone: PaymentRenewalAlertTone;
    daysToDue: number;
    message: string;
    iconName: PaymentRenewalAlertIcon;
    action: PaymentRenewalAlertAction;
    actionLabel: string;
}

/** Resumen (no sensible) del cobro automático que alimenta el banner de tarjeta. */
export interface CardAlertSummary {
    eligible: boolean;
    hasCard: boolean;
    enabled: boolean;
    status: string | null;
    last4: string | null;
}

const ALERT_DAYS_BEFORE_DUE = 5;
const DEFAULT_GRACE_DAYS = 1;
const RUNTIME_HIDE_PREFIX = 'payment-renewal-alert-hidden';

@Injectable({
    providedIn: 'root',
})
export class PaymentRenewalAlertService {
    buildAlertState(plansBundle: PlansBundle | null, runtimeKey: string, nowUtc: Moment = moment.utc()): PaymentRenewalAlertState {
        if (!this.hasPendingPayment(plansBundle)) {
            return this.getHiddenState();
        }

        const daysToDue = this.getDaysToDue(plansBundle, nowUtc);
        const inWarningWindow = daysToDue >= 1 && daysToDue <= ALERT_DAYS_BEFORE_DUE;
        // Keep banner visible for due day, grace day, and any overdue day.
        const inCriticalWindow = daysToDue <= 0;

        if (!inWarningWindow && !inCriticalWindow) {
            return this.getHiddenState();
        }

        if (inWarningWindow) {
            const hiddenForRuntime = this.isHiddenForRuntime(runtimeKey);
            if (hiddenForRuntime) {
                return this.getHiddenState();
            }

            const message =
                daysToDue === 1
                    ? 'Pago pendiente - Tu pago vence mañana. Regístralo para evitar la suspensión de tu cuenta.'
                    : `Ya se generó tu factura del mes. Recuerda pagarla antes del ${this.getFormattedDueDate(plansBundle.nextPayment)}.`;
            const iconName: PaymentRenewalAlertIcon = daysToDue === 1 ? 'alert-circle' : 'info';

            return {
                visible: true,
                dismissible: true,
                tone: 'warning',
                daysToDue,
                message,
                iconName,
                action: 'register',
                actionLabel: 'Registrar pago',
            };
        }

        return {
            visible: true,
            dismissible: false,
            tone: 'danger',
            daysToDue,
            message: this.getCriticalMessage(plansBundle, daysToDue),
            iconName: 'alert-circle',
            action: 'register',
            actionLabel: 'Registrar pago',
        };
    }

    /**
     * Estado del banner específico del cobro automático con tarjeta. Tiene
     * PRECEDENCIA sobre el mensaje genérico de "pago pendiente": para un tenant
     * con tarjeta guardada cuyo cobro falló, el mensaje correcto es actualizar/
     * verificar la tarjeta, no "registrar pago" (que sugiere pago móvil manual).
     *
     * Devuelve `null` si no aplica (no elegible, sin tarjeta, o tarjeta OK), y en
     * ese caso el banner cae al estado genérico (`buildAlertState`).
     */
    buildCardActionState(card: CardAlertSummary | null): PaymentRenewalAlertState | null {
        // Requisito base: tenant elegible con una tarjeta guardada. NO gateamos por
        // `enabled` aquí a propósito: los estados de acción (rechazo definitivo /
        // verificación 3DS) deben avisarse aunque el backend haya apagado el cobro
        // automático al marcar la tarjeta. Si `enabled` bloqueara esta rama, el
        // banner caería al mensaje genérico "Registrar pago" y el usuario nunca
        // sabría que debe reemplazar/verificar su tarjeta. La elegibilidad se
        // evalúa independientemente del estado problemático de la tarjeta.
        if (!card?.eligible || !card.hasCard) {
            return null;
        }

        if (card.status === 'requires_card_update') {
            const suffix = card.last4 ? ` terminada en ${card.last4}` : '';
            return {
                visible: true,
                dismissible: false,
                tone: 'danger',
                daysToDue: 0,
                message: `Tu tarjeta${suffix} fue rechazada. Actualízala para reactivar el cobro automático de tu suscripción.`,
                iconName: 'alert-circle',
                action: 'update-card',
                actionLabel: 'Actualizar tarjeta',
            };
        }

        if (card.status === 'requires_action') {
            return {
                visible: true,
                dismissible: false,
                tone: 'warning',
                daysToDue: 0,
                message: 'Tu pago necesita verificación con tu banco. Complétala para pagar tu suscripción.',
                iconName: 'alert-circle',
                action: 'pay-card',
                actionLabel: 'Verificar y pagar',
            };
        }

        return null;
    }

    buildRuntimeHideKey(tenantId?: string, userId?: string): string {
        return `${RUNTIME_HIDE_PREFIX}:${tenantId || 'anonymous-tenant'}:${userId || 'anonymous-user'}`;
    }

    hideForRuntime(runtimeKey: string): void {
        if (!runtimeKey) {
            return;
        }
        const storage = this.getStorage();
        if (!storage) {
            return;
        }
        try {
            storage.setItem(runtimeKey, 'true');
        } catch {
            // localStorage puede lanzar (quota llena, modo privado restrictivo, etc.). El cierre
            // del banner sigue siendo válido para la vista actual aunque la persistencia falle.
        }
    }

    clearRuntimeHide(runtimeKey: string): void {
        if (!runtimeKey) {
            return;
        }
        const storage = this.getStorage();
        if (!storage) {
            return;
        }
        try {
            storage.removeItem(runtimeKey);
        } catch {
            // ignorar: si no podemos limpiar el flag, el banner se mantendrá oculto hasta que el
            // estado del plan vuelva a "sin pago pendiente".
        }
    }

    /**
     * Elimina TODAS las claves de ocultamiento persistidas, sin importar tenant/usuario. Se invoca
     * al cerrar sesión para que, al iniciar una nueva sesión, el banner vuelva a mostrarse si se
     * cumplen las condiciones (el ocultamiento solo debe sobrevivir dentro de la misma sesión / F5).
     */
    clearAllRuntimeHides(): void {
        const storage = this.getStorage();
        if (!storage) {
            return;
        }
        try {
            const keysToRemove: string[] = [];
            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key && key.startsWith(RUNTIME_HIDE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => storage.removeItem(key));
        } catch {
            // ignorar: si no podemos limpiar los flags, en el peor caso el banner sigue oculto para
            // este usuario hasta que cambie el estado del plan; no es crítico para el cierre de sesión.
        }
    }

    isHiddenForRuntime(runtimeKey: string): boolean {
        if (!runtimeKey) {
            return false;
        }
        const storage = this.getStorage();
        if (!storage) {
            return false;
        }
        try {
            return storage.getItem(runtimeKey) === 'true';
        } catch {
            return false;
        }
    }

    isAfterGraceDay(plansBundle: PlansBundle | null, nowUtc: Moment = moment.utc()): boolean {
        if (!plansBundle?.nextPayment?.date) {
            return false;
        }

        const graceDays = this.getEffectiveGraceDays(plansBundle.nextPayment);
        const graceDate = moment.utc(plansBundle.nextPayment.date).startOf('day').add(graceDays, 'days');
        return moment.utc(nowUtc).startOf('day').isAfter(graceDate, 'day');
    }

    private getStorage(): Storage | null {
        try {
            if (typeof window === 'undefined') {
                return null;
            }
            return window.localStorage ?? null;
        } catch {
            return null;
        }
    }

    private hasPendingPayment(plansBundle: PlansBundle | null): boolean {
        if (!plansBundle?.nextPayment) {
            return false;
        }

        if (plansBundle.statusTenant === 'INACTIVE' || plansBundle.isInProcess) {
            return false;
        }

        const paymentStatus = (plansBundle.nextPayment.paymentStatus || '').toLowerCase();
        if (PROCESSING_PAYMENT_STATUSES.includes(paymentStatus)) {
            return false;
        }

        if (PAID_PAYMENT_STATUSES.includes(paymentStatus)) {
            return false;
        }

        return true;
    }

    private getDaysToDue(plansBundle: PlansBundle, nowUtc: Moment): number {
        const dueDate = moment.utc(plansBundle.nextPayment.date).startOf('day');
        return dueDate.diff(moment.utc(nowUtc).startOf('day'), 'days');
    }

    private getCriticalMessage(plansBundle: PlansBundle, daysToDue: number): string {
        if (daysToDue === 0) {
            return 'Pago pendiente - Tu pago vence hoy. Regístralo para evitar la suspensión de tu cuenta.';
        }

        const graceDays = this.getEffectiveGraceDays(plansBundle.nextPayment);
        const inGraceWindow = daysToDue < 0 && Math.abs(daysToDue) <= graceDays;
        if (inGraceWindow) {
            return 'Pago pendiente - Tu cuenta está en día de gracia.';
        }

        return 'Pago pendiente - Tu cuenta puede estar suspendida. Registra tu pago para reactivarla.';
    }

    private getFormattedDueDate(nextPayment: NextPayment): string {
        return moment.utc(nextPayment.date).locale('es').format('D [de] MMMM');
    }

    private getEffectiveGraceDays(nextPayment: NextPayment): number {
        return Number(nextPayment.graceDays ?? DEFAULT_GRACE_DAYS);
    }

    private getHiddenState(): PaymentRenewalAlertState {
        return {
            visible: false,
            dismissible: false,
            tone: 'warning',
            daysToDue: 0,
            message: '',
            iconName: 'alert-circle',
            action: 'register',
            actionLabel: 'Registrar pago',
        };
    }
}
